"use client";

import { ArrowRight, CircleCheck } from "lucide-react";
import { useState } from "react";
import { useThemeMode } from "../../contexts/ThemeContext";

import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Separator } from "../ui/separator";
import { Switch } from "../ui/switch";

interface PricingFeature {
  text: string;
}

interface PricingPlan {
  id: string;
  name: string;
  description: string;
  monthlyPrice: string;
  yearlyPrice: string;
  features: PricingFeature[];
  button: {
    text: string;
    url: string;
  };
}

interface Pricing2Props {
  heading?: string;
  description?: string;
  plans?: PricingPlan[];
}

const Pricing2 = ({
  heading = "",
  description = "Start your personal development journey with our flexible pricing options",
  plans = [
    {
      id: "free",
      name: "Free",
      description: "Perfect for getting started",
      monthlyPrice: "$0",
      yearlyPrice: "$0",
      features: [
        { text: "Basic talent tree" },
        { text: "Goal tracking" },
        { text: "Weekly progress reports" },
      ],
      button: {
        text: "Get Started",
        url: "/register",
      },
    },
    {
      id: "premium",
      name: "Premium",
      description: "For serious personal development",
      monthlyPrice: "$18.99",
      yearlyPrice: "$185",
      features: [
        { text: "Unlimited talent points" },
        { text: "Advanced analytics" },
        { text: "Custom talent trees" },
        { text: "Priority support" },
        { text: "Health assessment tools" },
        { text: "Weekly lock-in system" },
      ],
      button: {
        text: "Start Premium",
        url: "/register",
      },
    },
  ],
}: Pricing2Props) => {
  const [isYearly, setIsYearly] = useState(false);
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  return (
    <section 
      className="py-4"
      style={{
        background: 'transparent',
        position: 'relative',
      }}
    >
      <div className="container">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 text-center">
          {heading && (
            <h2 
              className="text-pretty text-4xl font-bold lg:text-6xl"
              style={{ 
                color: isDark ? '#ffffff' : '#000000',
                marginBottom: '2rem'
              }}
            >
              {heading}
            </h2>
          )}
          <p 
            className="lg:text-xl"
            style={{ 
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              marginBottom: '2rem'
            }}
          >
            {description}
          </p>
          <div 
            className="flex items-center gap-3 text-lg"
            style={{ 
              color: isDark ? '#ffffff' : '#000000',
              marginBottom: '2rem'
            }}
          >
            Monthly
            <Switch
              checked={isYearly}
              onCheckedChange={() => setIsYearly(!isYearly)}
            />
            Yearly
          </div>
          <div className="flex flex-col items-stretch gap-6 md:flex-row">
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className="flex w-80 flex-col justify-between text-left"
                style={{
                  background: isDark 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderRadius: '24px',
                  border: isDark
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: isDark
                    ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                    : '0 8px 32px rgba(31, 38, 135, 0.15)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = isDark
                    ? '0 12px 48px rgba(0, 0, 0, 0.5)'
                    : '0 12px 48px rgba(31, 38, 135, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0px)';
                  e.currentTarget.style.boxShadow = isDark
                    ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                    : '0 8px 32px rgba(31, 38, 135, 0.15)';
                }}
              >
                <CardHeader>
                  <CardTitle>
                    <p style={{ color: isDark ? '#ffffff' : '#000000', fontSize: '1.5rem', fontWeight: 600 }}>
                      {plan.name}
                    </p>
                  </CardTitle>
                  <p 
                    className="text-sm"
                    style={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}
                  >
                    {plan.description}
                  </p>
                  <span 
                    className="text-4xl font-bold"
                    style={{ 
                      color: plan.id === "premium" 
                        ? (isDark ? '#4ade80' : '#22c55e')
                        : (isDark ? '#ffffff' : '#000000')
                    }}
                  >
                    {isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                  </span>
                  <p style={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                    {isYearly ? (
                      plan.id === "premium" ? "Billed $185 annually" : "per year"
                    ) : (
                      plan.id === "premium" ? "Billed $18.99 a month" : "per month"
                    )}
                  </p>
                </CardHeader>
                <CardContent>
                  <Separator 
                    className="mb-6" 
                    style={{ 
                      background: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)' 
                    }} 
                  />
                  {plan.id === "premium" && (
                    <p 
                      className="mb-3 font-semibold"
                      style={{ color: isDark ? '#ffffff' : '#000000' }}
                    >
                      Everything in Free, and:
                    </p>
                  )}
                  <ul className="space-y-4">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <CircleCheck 
                          className="size-4" 
                          style={{ color: '#4ade80' }}
                        />
                        <span style={{ color: isDark ? '#ffffff' : '#000000' }}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto">
                  <Button 
                    asChild 
                    className="w-full"
                    style={{
                      background: plan.id === "premium"
                        ? (isDark ? 'rgba(74, 222, 128, 0.2)' : '#4ade80')
                        : (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'),
                      border: plan.id === "premium"
                        ? '1px solid #4ade80'
                        : (isDark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.2)'),
                      color: plan.id === "premium"
                        ? (isDark ? '#4ade80' : '#000000')
                        : (isDark ? '#ffffff' : '#000000'),
                      borderRadius: '16px',
                      padding: '16px 32px',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <a href={plan.button.url}>
                      {plan.button.text}
                      <ArrowRight className="ml-2 size-4" />
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export { Pricing2 };
